import { element } from "./dom.js";
import type { PartState } from "./part-state.js";
import { record, strings } from "./sheet-data.js";
import {
  type FlowPart,
  type FlowRow,
  SheetFlow,
  sheetCue,
  sheetHanzi,
  columnWidth,
  sheetNumber,
  sheetRows,
  sheetSpannedRows,
} from "./sheet-flow.js";
import {
  blackInk,
  type GlyphLibrary,
  type SquareContent,
} from "./worksheet-drawing.js";
import type { SheetLabels } from "./worksheet-page.js";
import { pageRoom, sized } from "./worksheet-page.js";
import type { WorksheetPaper } from "./worksheet-state.js";

/**
 * The vocabulary sheet 词汇, as pages ready to print.
 *
 * Three parts and the answers, every item with one answer named by its cue.
 * **Make words** 组词 gives a character with three of its words, the
 * character in its square and the rest of each word to write. **Collocations**
 * 搭配 gives a phrase with a word taken out. **Same and opposite** 近义反义
 * asks for a synonym and an antonym of a word. What each word is asked with
 * is worked out at build time, in `src/content/worksheets/vocabulary-data.ts`.
 */

/** The parts a vocabulary sheet prints. */
export type VocabularyPart = "zuci" | "collocations" | "opposites" | "key";

/** A word, its reading and its cue. */
export type VocabularyWord = readonly [
  word: string,
  reading: string,
  cue: string,
];

/** One character's 组词 candidates. */
export interface VocabularyCharacter {
  readonly reading: string;
  readonly cue: string;
  readonly words: readonly VocabularyWord[];
}

/** What the sheet can ask about one word. */
export interface VocabularyFacts {
  readonly reading: string;
  readonly cue: string;
  readonly same?: readonly [string, string];
  readonly opposite?: readonly [string, string];
  readonly collocations: readonly (readonly [string, string, string])[];
}

/** The data file. */
export interface VocabularyFile {
  readonly characters: ReadonlyMap<string, VocabularyCharacter>;
  readonly words: ReadonlyMap<string, VocabularyFacts>;
}

function pair(value: unknown): readonly [string, string] | undefined {
  const parts = strings(value);

  return parts?.length === 2 ? [parts[0]!, parts[1]!] : undefined;
}

function triples(
  value: unknown,
): readonly (readonly [string, string, string])[] {
  return Array.isArray(value)
    ? value.flatMap((one: unknown) => {
        const parts = strings(one);

        return parts?.length === 3
          ? [[parts[0]!, parts[1]!, parts[2]!] as const]
          : [];
      })
    : [];
}

/** A character's 组词 read defensively, or nothing for one that is not. */
function readCharacter(value: unknown): VocabularyCharacter | undefined {
  const entry = record(value);

  return typeof entry?.["reading"] === "string" &&
    typeof entry["cue"] === "string"
    ? {
        reading: entry["reading"],
        cue: entry["cue"],
        words: triples(entry["words"]),
      }
    : undefined;
}

/** A word's facts read defensively, or nothing for ones that are not. */
function readFacts(value: unknown): VocabularyFacts | undefined {
  const entry = record(value);
  if (
    typeof entry?.["reading"] !== "string" ||
    typeof entry["cue"] !== "string"
  ) {
    return undefined;
  }
  const same = pair(entry["same"]);
  const opposite = pair(entry["opposite"]);

  return {
    reading: entry["reading"],
    cue: entry["cue"],
    ...(same !== undefined && { same }),
    ...(opposite !== undefined && { opposite }),
    collocations: triples(entry["collocations"]),
  };
}

/** Each of an object's values read, keeping the ones that could be. */
function readAll<Value>(
  values: Readonly<Record<string, unknown>>,
  read: (value: unknown) => Value | undefined,
): ReadonlyMap<string, Value> {
  const kept = new Map<string, Value>();
  for (const [key, value] of Object.entries(values)) {
    const one = read(value);
    if (one !== undefined) {
      kept.set(key, one);
    }
  }

  return kept;
}

/** The whole data file read defensively, or nothing for a body that is not one. */
export function vocabularyFile(body: unknown): VocabularyFile | undefined {
  const file = record(body);
  const characters = record(file?.["characters"]);
  const words = record(file?.["words"]);
  if (characters === undefined || words === undefined) {
    return undefined;
  }

  return {
    characters: readAll(characters, readCharacter),
    words: readAll(words, readFacts),
  };
}

/**
 * Which items the sheet can ask for, published at `keys.json`.
 *
 * Every character listed has 组词 words, every collocation word has a
 * collocation and every pair word has a synonym and an antonym, which is
 * what lets `vocabularyNeeds` count the questions without the items.
 */
export interface VocabularyKeys {
  readonly characters: ReadonlySet<string>;
  readonly collocations: ReadonlySet<string>;
  readonly pairs: ReadonlySet<string>;
}

/** The keys read defensively, or nothing for a body that is not them. */
export function vocabularyKeys(body: unknown): VocabularyKeys | undefined {
  const keys = record(body);
  const characters = strings(keys?.["characters"]);
  const collocations = strings(keys?.["collocations"]);
  const pairs = strings(keys?.["pairs"]);

  return characters === undefined ||
    collocations === undefined ||
    pairs === undefined
    ? undefined
    : {
        characters: new Set(characters),
        collocations: new Set(collocations),
        pairs: new Set(pairs),
      };
}

/** One item, published at `data/<key>.json`: a character's 组词, a word's facts or both. */
export interface VocabularyShard {
  readonly character?: VocabularyCharacter;
  readonly word?: VocabularyFacts;
}

/**
 * An item read defensively, or nothing for a body that is not one. An item
 * holding neither a character nor a word is not one, since the build never
 * publishes one and a sheet drawn without it would leave its questions out.
 */
export function vocabularyShard(body: unknown): VocabularyShard | undefined {
  const item = record(body);
  const character = readCharacter(item?.["character"]);
  const word = readFacts(item?.["word"]);
  if (character === undefined && word === undefined) {
    return undefined;
  }

  return {
    ...(character !== undefined && { character }),
    ...(word !== undefined && { word }),
  };
}

/** The data file made of the items fetched. */
export function vocabularyFileOf(
  items: ReadonlyMap<string, VocabularyShard>,
): VocabularyFile {
  const characters = new Map<string, VocabularyCharacter>();
  const words = new Map<string, VocabularyFacts>();
  for (const [key, item] of items) {
    if (item.character !== undefined) {
      characters.set(key, item.character);
    }
    if (item.word !== undefined) {
      words.set(key, item.word);
    }
  }

  return { characters, words };
}

/** A 组词 slot: a word, where its character sits, its reading and cue. */
export interface ZuciSlot {
  readonly word: string;
  readonly at: number;
  readonly reading: string;
  readonly cue: string;
}

/** A character and its slots. */
export interface ZuciItem {
  readonly character: string;
  readonly reading: string;
  readonly cue: string;
  readonly slots: readonly ZuciSlot[];
}

/** A collocation with its word taken out. */
export interface CollocationItem {
  readonly word: string;
  readonly reading: string;
  readonly before: string;
  readonly after: string;
  readonly english: string;
}

/** A word with a synonym and an antonym to write. */
export interface OppositeItem {
  readonly word: string;
  readonly reading: string;
  readonly cue: string;
  readonly same: readonly [string, string];
  readonly opposite: readonly [string, string];
}

/** What the sheet asks about these words. */
export interface VocabularyChoice {
  readonly zuci: readonly ZuciItem[];
  readonly collocations: readonly CollocationItem[];
  readonly opposites: readonly OppositeItem[];
  /** The words nothing on the sheet asks about. */
  readonly unasked: readonly string[];
}

/** How many words a character is asked to make. */
const slotsPerCharacter = 3;

/** The most items each part takes. */
export const vocabularyLimits = {
  zuci: 30,
  collocations: 45,
  opposites: 30,
} as const;

const han = /\p{Script=Han}/u;

/**
 * What to ask about these words.
 *
 * A character's words are drawn from the sheet's own words first, so a List
 * revises itself, and then from its 词典 entry's 组词, easiest first.
 * @param words - The sheet's words, in the order they were given.
 */
export function vocabularyChoice(
  file: VocabularyFile,
  words: readonly string[],
): VocabularyChoice {
  const given = [...new Set(words)];
  const asked = new Set<string>();
  const zuci: ZuciItem[] = [];
  const seen = new Set<string>();

  for (const word of given) {
    for (const character of Array.from(word)) {
      if (
        !han.test(character) ||
        seen.has(character) ||
        zuci.length >= vocabularyLimits.zuci
      ) {
        continue;
      }
      seen.add(character);
      const entry = file.characters.get(character);
      if (entry === undefined) {
        continue;
      }
      const candidates = [
        ...entry.words.filter(([candidate]) => given.includes(candidate)),
        ...entry.words.filter(([candidate]) => !given.includes(candidate)),
      ].slice(0, slotsPerCharacter);
      if (candidates.length === 0) {
        continue;
      }
      asked.add(word);
      zuci.push({
        character,
        reading: entry.reading,
        cue: entry.cue,
        slots: candidates.map(([candidate, reading, cue]) => ({
          word: candidate,
          at: Array.from(candidate).indexOf(character),
          reading,
          cue,
        })),
      });
    }
  }

  const collocations: CollocationItem[] = [];
  const opposites: OppositeItem[] = [];
  for (const word of given) {
    const facts = file.words.get(word);
    if (facts === undefined) {
      continue;
    }
    const collocation = facts.collocations[0];
    if (
      collocation !== undefined &&
      collocations.length < vocabularyLimits.collocations
    ) {
      asked.add(word);
      collocations.push({
        word,
        reading: facts.reading,
        before: collocation[0],
        after: collocation[1],
        english: collocation[2],
      });
    }
    if (
      facts.same !== undefined &&
      facts.opposite !== undefined &&
      opposites.length < vocabularyLimits.opposites
    ) {
      asked.add(word);
      opposites.push({
        word,
        reading: facts.reading,
        cue: facts.cue,
        same: facts.same,
        opposite: facts.opposite,
      });
    }
  }

  return {
    zuci,
    collocations,
    opposites,
    unasked: given.filter((word) => !asked.has(word)),
  };
}

/**
 * The items `vocabularyChoice` reads for these words, worked out from the
 * keys alone.
 *
 * It follows the choice step for step: the first of the words' characters
 * that have 组词, up to the 组词 limit, and each word whose collocation or
 * pair would still be taken under its part's limit. A word whose questions
 * would all go over the limits is left out, and so is every key the choice
 * would pass over, so the sheet drawn from these items is the one the whole
 * file would draw.
 * @param words - The sheet's words, in the order they were given.
 */
export function vocabularyNeeds(
  keys: VocabularyKeys,
  words: readonly string[],
): readonly string[] {
  const given = [...new Set(words)];
  const needed: string[] = [];
  const seen = new Set<string>();
  let zuci = 0;
  for (const word of given) {
    for (const character of Array.from(word)) {
      if (
        !han.test(character) ||
        seen.has(character) ||
        zuci >= vocabularyLimits.zuci
      ) {
        continue;
      }
      seen.add(character);
      if (keys.characters.has(character)) {
        zuci += 1;
        needed.push(character);
      }
    }
  }

  let collocations = 0;
  let opposites = 0;
  for (const word of given) {
    let need = false;
    if (
      keys.collocations.has(word) &&
      collocations < vocabularyLimits.collocations
    ) {
      collocations += 1;
      need = true;
    }
    if (keys.pairs.has(word) && opposites < vocabularyLimits.opposites) {
      opposites += 1;
      need = true;
    }
    if (need) {
      needed.push(word);
    }
  }

  return needed;
}

/** Each part's rows, in millimetres. */
export const vocabularyHeights = {
  zuci: 21,
  collocations: 19,
  opposites: 22,
  key: 6,
} as const;

/**
 * The least each part's rows can be drawn at, and the source line's, on a
 * page drawn closer to take the last of its part. Measured in the browser,
 * where a 组词 row holds 16.8 mm, a collocation 14.5 mm, a pair to write
 * 18.1 mm, an answer 5 mm and the source line 3.2 mm.
 */
export const vocabularyLeast = {
  zuci: 18,
  collocations: 16,
  opposites: 19,
  key: 5.5,
  source: 4,
} as const;

/** The squares each part's answers are written in. */
const squares = { zuci: 12, collocations: 10, opposites: 10 } as const;

/** How many collocations a row holds when every phrase is short enough. */
const collocationColumns = 3;

/** The size of a collocation's printed characters, in millimetres. Each is as wide as it is tall. */
const collocationText = 5;

/** The number before a collocation's phrase and the gap after it, as `.cb-wv-item` has them. */
const collocationLead = 6 + 1.2;

/**
 * How wide a collocation's line is on paper, its number and phrase together.
 * The printed characters are a square each, so the width is counted rather
 * than measured, the way the rows' heights are.
 */
export function collocationWidth(item: CollocationItem): number {
  const printed = Array.from(item.before + item.after).length;

  return (
    collocationLead +
    printed * collocationText +
    Array.from(item.word).length * squares.collocations
  );
}

/**
 * How many of a row's columns a collocation takes on this paper. Most fit in
 * a third of the row. 一毛不拔 asked inside a phrase ending 的铁公鸡 does not,
 * and runs past its column unless it takes two.
 */
export function collocationSpan(
  item: CollocationItem,
  paper: WorksheetPaper,
): number {
  const room = pageRoom(paper).width;
  const width = collocationWidth(item);
  for (let span = 1; span < collocationColumns; span += 1) {
    if (width <= columnWidth(room, collocationColumns, span)) {
      return span;
    }
  }

  return collocationColumns;
}

/** One answer in the key: its number and the answer, marked. */
function answer(number: number, children: readonly Node[]): HTMLElement {
  return element("div", {
    className: "cb-wf-key-item",
    children: [
      sheetNumber(number),
      element("span", {
        className: "cb-wf-hz",
        attributes: { lang: "zh-Hans" },
        children,
      }),
    ],
  });
}

/** An answer's own words, underlined in the faint red. */
function mark(text: string): HTMLElement {
  return element("span", { className: "cb-wf-mark", text });
}

function text(value: string): Text {
  return document.createTextNode(value);
}

/** What drawing the sheet needs. */
export interface VocabularySheetProperties {
  /** Holds the outlines of each 组词 character, which its slots draw. */
  readonly library: GlyphLibrary;
}

/** Draws vocabulary sheets. */
export class VocabularySheet {
  readonly #library: GlyphLibrary;

  constructor(properties: VocabularySheetProperties) {
    this.#library = properties.library;
  }

  /** Every page of the sheet, leaving out the parts not asked for. */
  pages(
    state: PartState<VocabularyPart>,
    choice: VocabularyChoice,
    labels: SheetLabels,
  ): readonly HTMLElement[] {
    let number = 0;
    const zuci = state.zuci ? choice.zuci : [];
    const collocations = state.collocations ? choice.collocations : [];
    const opposites = state.opposites ? choice.opposites : [];
    const zuciNumbers = zuci.map((item) => item.slots.map(() => (number += 1)));
    const collocationStart = number + 1;
    number += collocations.length;
    const oppositeStart = number + 1;

    const parts: FlowPart[] = [];
    let part = 0;
    if (zuci.length > 0) {
      part += 1;
      parts.push({
        number: part,
        title: "Make words",
        chinese: "组词",
        note: "Each word contains the printed character. Complete the words using the pinyin and English. Write one character in each empty square.",
        rows: zuci.map((item, at) => this.#zuciRow(item, zuciNumbers[at]!)),
      });
    }
    if (collocations.length > 0) {
      part += 1;
      parts.push({
        number: part,
        title: "Collocations",
        chinese: "搭配",
        note: "Write the missing word, one character in each square. The pinyin is for the missing word. The English gives the meaning of the whole phrase.",
        rows: sheetSpannedRows(
          collocations.map((item, at) => ({
            node: this.#collocation(item, collocationStart + at),
            span: collocationSpan(item, state.paper),
          })),
          collocationColumns,
          vocabularyHeights.collocations,
          vocabularyLeast.collocations,
        ),
      });
    }
    if (opposites.length > 0) {
      part += 1;
      parts.push({
        number: part,
        title: "Same and opposite",
        chinese: "近义反义",
        note: "Write a word with the same meaning and one with the opposite meaning. The pinyin tells you which words to write.",
        rows: sheetRows(
          opposites.map((item, at) => this.#opposite(item, oppositeStart + at)),
          2,
          vocabularyHeights.opposites,
          "",
          vocabularyLeast.opposites,
        ),
      });
    }
    if (state.key && parts.length > 0) {
      parts.push(
        ...this.#key({
          zuci,
          zuciNumbers,
          collocations,
          collocationStart,
          opposites,
          oppositeStart,
        }),
      );
    }

    return new SheetFlow({
      paper: state.paper,
      kind: "Vocabulary",
      kindChinese: "词汇",
      parts,
      labels,
    }).pages();
  }

  #squares(word: string, size: number, at?: number): SVGElement {
    const contents = Array.from(word, (letter, index): SquareContent =>
      index === at
        ? { kind: "glyph", character: letter, ink: blackInk }
        : { kind: "empty" },
    );

    return this.#library.row(size, contents, undefined, "tracing");
  }

  #zuciRow(item: ZuciItem, numbers: readonly number[]): FlowRow {
    const character = element("div", {
      className: "cb-wv-character",
      children: [sheetHanzi(item.character), sheetCue(item.reading, item.cue)],
    });
    const slots = item.slots.map((slot, at) =>
      element("div", {
        className: "cb-wv-slot",
        children: [
          sheetNumber(numbers[at]!),
          this.#squares(slot.word, squares.zuci, slot.at),
          sheetCue(slot.reading, slot.cue),
        ],
      }),
    );
    const row = element("div", {
      className: "cb-wf-row cb-wv-zuci",
      children: [character, ...slots],
    });

    return {
      node: sized(row, { height: vocabularyHeights.zuci }),
      height: vocabularyHeights.zuci,
      least: vocabularyLeast.zuci,
    };
  }

  #collocation(item: CollocationItem, number: number): HTMLElement {
    const phrase = sized(
      element("span", {
        className: "cb-wv-phrase",
        children: [
          ...(item.before === "" ? [] : [sheetHanzi(item.before)]),
          this.#squares(item.word, squares.collocations),
          ...(item.after === "" ? [] : [sheetHanzi(item.after)]),
        ],
      }),
      { "font-size": collocationText },
    );

    return element("div", {
      className: "cb-wv-item",
      children: [
        sheetNumber(number),
        phrase,
        sheetCue(item.reading, item.english),
      ],
    });
  }

  #opposite(item: OppositeItem, number: number): HTMLElement {
    const ask = (
      label: string,
      [word, reading]: readonly [string, string],
    ): HTMLElement =>
      element("div", {
        className: "cb-wv-ask",
        children: [
          element("span", { className: "cb-wv-ask-label", text: label }),
          this.#squares(word, squares.opposites),
          sheetCue(reading),
        ],
      });

    return element("div", {
      className: "cb-wv-opposite",
      children: [
        sheetNumber(number),
        element("div", {
          className: "cb-wv-word",
          children: [sheetHanzi(item.word), sheetCue(item.reading, item.cue)],
        }),
        ask("Same 近义", item.same),
        ask("Opposite 反义", item.opposite),
      ],
    });
  }

  #key(sheet: {
    readonly zuci: readonly ZuciItem[];
    readonly zuciNumbers: readonly (readonly number[])[];
    readonly collocations: readonly CollocationItem[];
    readonly collocationStart: number;
    readonly opposites: readonly OppositeItem[];
    readonly oppositeStart: number;
  }): readonly FlowPart[] {
    const parts: FlowPart[] = [];
    let part = 0;
    if (sheet.zuci.length > 0) {
      part += 1;
      parts.push({
        number: part,
        title: "Make words",
        chinese: "组词",
        rows: sheetRows(
          sheet.zuci.flatMap((item, at) =>
            item.slots.map((slot, index) => {
              const shown = answer(sheet.zuciNumbers[at]![index]!, [
                mark(slot.word),
              ]);
              shown.append(
                element("span", {
                  className: "cb-wf-en",
                  text: slot.reading,
                  attributes: { lang: "zh-Latn-CN-pinyin" },
                }),
              );

              return shown;
            }),
          ),
          5,
          vocabularyHeights.key,
          "",
          vocabularyLeast.key,
        ),
        startsPage: true,
      });
    }
    if (sheet.collocations.length > 0) {
      part += 1;
      parts.push({
        number: part,
        title: "Collocations",
        chinese: "搭配",
        rows: sheetRows(
          sheet.collocations.map((item, at) =>
            answer(sheet.collocationStart + at, [
              text(item.before),
              mark(item.word),
              text(item.after),
            ]),
          ),
          3,
          vocabularyHeights.key,
          "",
          vocabularyLeast.key,
        ),
        startsPage: parts.length === 0,
      });
    }
    if (sheet.opposites.length > 0) {
      part += 1;
      parts.push({
        number: part,
        title: "Same and opposite",
        chinese: "近义反义",
        rows: [
          ...sheetRows(
            sheet.opposites.map((item, at) =>
              answer(sheet.oppositeStart + at, [
                text(`${item.word}　近义 `),
                mark(item.same[0]),
                text("　反义 "),
                mark(item.opposite[0]),
              ]),
            ),
            2,
            vocabularyHeights.key,
            "",
            vocabularyLeast.key,
          ),
        ],
        startsPage: parts.length === 0,
      });
    }
    const source = element("p", {
      className: "cb-wf-source",
      text: "Words from the Chinese Boost dictionary. Collocations, synonyms and antonyms from 《现代汉语同义近义反义…例解大全》.",
    });
    const last = parts.at(-1);
    if (last !== undefined) {
      parts[parts.length - 1] = {
        ...last,
        rows: [
          ...last.rows,
          {
            node: sized(source, { height: vocabularyHeights.key }),
            height: vocabularyHeights.key,
            least: vocabularyLeast.source,
          },
        ],
      };
    }

    return parts;
  }
}
